import { formatDisplayDateTime } from "../../utils/dateUtils";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { ActivityMedical, Result } from "../../interface/Activitymedical";
import { ActivityMedicalAxiosInstance } from "../../Network/Activitymedical";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ActivitySelector from "../Shared/ActivityType";
import { Option } from "../../interface/Option";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

// ─── Role Helper ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);


// Keep linked group status separate from the status of its parent group.
const linkedGroupInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  const active = item.is_active ?? item.isActive ?? item.active;
  const status = String(item.status ?? item.Status ?? "").toLowerCase();
  return item.__inactive === true || item.isInactive === true ||
    active === false || active === 0 || active === "false" || active === "0" ||
    status === "inactive" || status === "disabled";
};
const linkedGroupOption = (g: any): Option => ({
  ...(typeof g === "object" && g !== null ? g : {}),
  value: (typeof g === "object" && g !== null ? (g.id ?? g.value) : g),
  label: (typeof g === "object" && g !== null ? (g.name ?? g.label ?? String(g.id ?? g.value)) : String(g)),
  __inactive: linkedGroupInactive(g),
} as Option);
const linkedGroupChipStyle = (g: any, activeBackground: string, activeColor: string, activeBorder: string) => ({
  background: linkedGroupInactive(g) ? "#e5e7eb" : activeBackground,
  color: linkedGroupInactive(g) ? "#334155" : activeColor,
  border: `1px solid ${linkedGroupInactive(g) ? "#9ca3af" : activeBorder}`,
});
const linkedGroupText = (g: any) => `${typeof g === "object" && g !== null ? (g.name ?? g.label ?? g.id ?? "") : g}${linkedGroupInactive(g) ? " · Inactive" : ""}`;
const parseGroupResponse = (payload: any): any | null => {
  const candidate = payload?.result ?? payload?.data ?? payload?.item ?? payload;
  if (Array.isArray(candidate)) return candidate[0] ?? null;
  if (Array.isArray(candidate?.data)) return candidate.data[0] ?? null;
  return candidate && typeof candidate === "object" && (candidate.id != null || candidate.is_active != null || candidate.status != null) ? candidate : null;
};
// Refresh the status independently of the parent record. If the endpoint cannot
// confirm a status, retain the status already returned by the relation.
const hydrateLinkedGroups = async (groups: any[], client: any, endpoint: string): Promise<any[]> =>
  Promise.all((Array.isArray(groups) ? groups : []).map(async (group) => {
    const id = typeof group === "object" && group !== null ? (group.id ?? group.value) : group;
    if (id == null || id === "") return group;
    try {
      const response = await client.get(`${endpoint}/${id}`);
      const current = parseGroupResponse(response.data);
      if (!current || String(current.id ?? "") !== String(id)) return group;
      return { ...(typeof group === "object" && group !== null ? group : { id }), ...current };
    } catch {
      // Some get-by-ID routes exclude inactive rows. Try the inactive list,
      // but never infer "inactive" merely from a failed request.
      try {
        const response = await client.get(endpoint, { params: { page: 1, limit: 100, is_active: "false", is_deleted: "false" } });
        const payload = response.data;
        const root = payload?.result ?? payload?.data ?? payload;
        const entries = Array.isArray(root) ? root : Array.isArray(root?.data) ? root.data : [];
        const matching = entries.find((entry: any) => String(entry?.id) === String(id));
        if (matching) return { ...(typeof group === "object" && group !== null ? group : { id }), ...matching, is_active: false, __inactive: true };
      } catch { /* Preserve known relation status when neither source is available. */ }
      return group;
    }
  }));

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── Enhanced backend contract helpers ──────────────────────────────────────
const toIdArray = (options: Option[]): number[] =>
  options
    .map((option) => Number(option.value))
    .filter((id) => Number.isInteger(id) && id > 0);

const ACTIVITY_TYPE_ID_TO_NAME: Record<number, string> = {
  1: "CPT",
  2: "Dental",
  3: "HCPCS",
  4: "Drugs",
};

const getActivitiesWithTypes = (item: any): Array<{ code: string | number; type: string }> => {
  const codes = Array.isArray(item?.activity_code)
    ? item.activity_code
    : Array.isArray(item?.activities)
      ? item.activities
      : [];
  const numericTypes = Array.isArray(item?.types) ? item.types : [];
  const activityTypes = Array.isArray(item?.activity_types) ? item.activity_types : [];
  const legacyType = item?.activity_type || item?.type || "";

  if (activityTypes.length > 0 && typeof activityTypes[0] === "object") {
    return activityTypes
      .filter((entry: any) => entry?.code != null)
      .map((entry: any) => ({ code: entry.code, type: entry.type || legacyType || "Unknown" }));
  }

  return codes.map((code: string | number, index: number) => {
    let type = legacyType || "Unknown";
    if (numericTypes.length === codes.length && numericTypes[index] != null) {
      type = ACTIVITY_TYPE_ID_TO_NAME[Number(numericTypes[index])] || type;
    } else if (numericTypes.length === 1) {
      type = ACTIVITY_TYPE_ID_TO_NAME[Number(numericTypes[0])] || type;
    } else if (activityTypes.length === codes.length && typeof activityTypes[index] === "string") {
      type = activityTypes[index];
    } else if (activityTypes.length === 1 && typeof activityTypes[0] === "string") {
      type = activityTypes[0];
    }
    return { code, type };
  });
};

const getActivityCodes = (item: any): Array<string | number> =>
  getActivitiesWithTypes(item).map(({ code }) => code);

const getPrimaryActivityType = (item: any): string => {
  const pairs = getActivitiesWithTypes(item);
  return pairs.find(({ type }) => type && type !== "Unknown")?.type || item?.activity_type || item?.type || "";
};

const ActivityMedicalGroup: React.FC = () => {
  useQaAutomationIds("activity-medical-group");
  // Main data state
  const [items, setItems] = useState<ActivityMedical>({ result: [] });

  // ─── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  // Form states
  const [Name, setName] = useState<string>("");
  const [Description, setDescription] = useState<string>("");
  const [activitiesByType, setActivitiesByType] = useState<Record<string, Option[]>>({});
  const [ActivityType, setActivityType] = useState<string>("");
  const [medicalActivityGroups, setMedicalActivityGroups] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);

  // UI control states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearListOfActivities, setClearListOfActivities] = useState(false);
  const [clearactivitymedicalGroups, setClearActivityMedicalGroups] = useState(false);
  const [activityTypeisValid, setActivityTypeIsValid] = useState<boolean>(true);
  const [viewCodes, setViewCodes] = useState<string[]>([]);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      CPT:    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
      HCPCS:  { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
      Dental: { bg: "#faf5ff", border: "#e9d5ff", text: "#7e22ce" },
      Drugs:  { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c" },
    };
    return colors[type] || { bg: "#f1f5f9", border: "#e2e8f0", text: "#334155" };
  };

  // Get token from localStorage
  const token = localStorage.getItem("token") || "";
  // dialog for details
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});


  const { toast } = useToast();

  useEffect(() => { fetchUsers(); }, []);

  // Dialog details control handlers
  // Always refresh the record from the backend before View/Edit.
  const fetchActivityMedicalGroupDetails = async (id: string | number, fallback?: Result): Promise<Result> => {
    const response = await ActivityMedicalAxiosInstance.get(`getActivityMedicalGroup/${id}`);
    const raw =
      response.data?.result ??
      response.data?.getActivityMedicalGroup ??
      response.data?.getActivityMedicalGroups ??
      response.data?.data ??
      response.data?.item ??
      response.data;
    const record = Array.isArray(raw) ? raw[0] : raw;
    if (!record || typeof record !== "object") {
      throw new Error("Record details were not returned by the backend.");
    }
    const merged: any = { ...(fallback || {}), ...record };
    merged.medical_groups = await hydrateLinkedGroups(merged.medical_groups || [], ActivityMedicalAxiosInstance, "getActivityMedicalGroup");
    return merged as Result;
  };

  const handleOpenDialog = async (item: Result) => {
    try {
      setSelectedItem(await fetchActivityMedicalGroupDetails(item.id, item));
    } catch (error) {
      console.error("Failed to load Activity Medical Group by ID for view:", error);
      setSelectedItem(item);
      toast({ title: "Warning", description: "Could not refresh group details. Using current row data." });
    }
  };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  // Handlers for form inputs
  const handleActivityTypeChange = (ActivityType: string) => {
    setActivityType(ActivityType);
  };

  const handleActivitiesChange = (selectedOptions: Option[]) => {
    if (!ActivityType) return;
    setActivitiesByType(prev => ({
      ...prev,
      [ActivityType]: selectedOptions
    }));
  };

  const handleMedicalActivityGroupsChange = (selectedOptions: Option[]) => {
    setMedicalActivityGroups(selectedOptions);
  };

  // Clear form function
  const clear = () => {
    setName("");
    setDescription("");
    setActivitiesByType({});
    setActivityType("");
    setMedicalActivityGroups([]);
    setIsActive(true);
  };

  // Dialog control handlers
  const handleAddClick = () => {
    setEditIndex(null);
    clear();
    setIsDialogOpen(true);
  };

  const handleEditClick = async (item: Result) => {
    try {
      const details = await fetchActivityMedicalGroupDetails(item.id, item);
      handleEdit(details);
    } catch (error) {
      console.error("Failed to load Activity Medical Group by ID for edit:", error);
      handleEdit(item);
      toast({ title: "Warning", description: "Could not refresh group details. Using current row data." });
    }
    setIsDialogOpen(true);
  };

  // ─── API Functions ────────────────────────────────────────────────────────


  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityMedicalAxiosInstance.get(
        "getActivityMedicalGroup",
        {
          params: {
            page,
            limit,
            name: filters.search.trim() || undefined,
            activity: filters.search.trim() || undefined,
            disease: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    sortRows: (rows) => [...rows].sort((a: any, b: any) => Number(b.id) - Number(a.id)),
    onError: (error) => {
      console.error("Failed to fetch Activity Medical groups", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Medical groups",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─── Toggle Active ────────────────────────────────────────────────────────
  const toggleActive = async (row: any, force = false): Promise<void> => {
    try {
      const medicalGroupsForRequest = normalizeMedicalGroupsForRequest(row.medical_groups || []);

      const updatedItem = {
        id: row.id,
        name: row.name,
        description: row.description || "",
        activity_code: getActivityCodes(row),
        medical_groups: medicalGroupsForRequest,
        is_active: !row.is_active,
      };

      const response = await ActivityMedicalAxiosInstance.put("updateActivityMedicalGroup", {
        ...updatedItem,
        force,
      });

      if (response.data.success) {
        setItems((prev) => ({
          result: prev.result.map((i) =>
            i.id === row.id ? { ...i, is_active: !row.is_active } : i
          ),
        }));
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({ title: "Success", description: `Status updated to ${!row.is_active ? "Active" : "Deactivate"}` });
      } else {
        setForceId(row.id);
        setForceAction("status");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used") ||
        errMsg.toLowerCase().includes("referenced") ||
        errMsg.toLowerCase().includes("cannot")
      ) {
        setForceId(row.id);
        setForceAction("status");
        setForceError(errMsg || "This record is used elsewhere.");
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
      }
    }
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityMedicalAxiosInstance.delete(
        `softDeleteActivityMedicalGroup/${id}`,
        { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Medical Group deleted successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used") ||
        errMsg.toLowerCase().includes("referenced") ||
        errMsg.toLowerCase().includes("cannot")
      ) {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityMedicalAxiosInstance.patch(
        `restoreActivityMedicalGroup/${id}`,
        { token }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Medical Group restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteActivityMedicalGroup/${id}?force=true` : `deleteActivityMedicalGroup/${id}`;
      const response = await ActivityMedicalAxiosInstance.delete(url, { data: { token, force } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Medical Group permanently deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ─── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityMedicalAxiosInstance.delete(
        `softDeleteActivityMedicalGroup/${id}?force=true`,
        { data: { token } }
      );
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Medical Group force deleted successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const addItem = async (): Promise<void> => {
    if (!Name.trim()) {
      toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" });
      return;
    }
    if (Name.trim().length < 3) {
      toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" });
      return;
    }
    const activityCodes = Object.values(activitiesByType)
      .flat()
      .map((option) => option.value)
      .filter((value) => value != null && String(value).trim() !== "");
    const ValuesActivityMedical = toIdArray(medicalActivityGroups);

    // Either an Activity Code OR a Medical Group satisfies the requirement.
    if (activityCodes.length === 0 && ValuesActivityMedical.length === 0) {
      toast({
        title: "Error",
        description: "Select at least one Activity Code or Medical Group.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Enhanced backend contract: activity_code + referenced group IDs.
      const payload = {
        name: Name.trim(),
        description: Description,
        activity_code: activityCodes,
        medical_groups: ValuesActivityMedical,
        is_active: isActive,
      };
      const response = await ActivityMedicalAxiosInstance.post("addNewActivityMedicalGroup", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activity Medical Group added successfully." });
      } else {
        const errors = response.data.errors || [];
        const errorMessages = errors.map((error: any) => error.error).join("\n");
        toast({ title: "Error", description: errorMessages || response.data.message || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setName(item.name);

    const grouped: Record<string, Option[]> = {};
    getActivitiesWithTypes(item).forEach(({ code, type }) => {
      const resolvedType = type && type !== "Unknown" ? type : getPrimaryActivityType(item);
      if (!resolvedType) return;
      grouped[resolvedType] = grouped[resolvedType] || [];
      grouped[resolvedType].push({ value: code, label: code.toString() });
    });
    setActivitiesByType(grouped);
    setActivityType(getPrimaryActivityType(item));

    const MedicalActivityGroupsData = Array.isArray(item.medical_groups) ? item.medical_groups : [];
    setMedicalActivityGroups(
      MedicalActivityGroupsData.map(linkedGroupOption)
    );
    setIsActive(item.is_active);
    setDescription(item.description);
    setEditIndex(item.id);
  };

  const normalizeMedicalGroupsForRequest = (groups: any[] = []): number[] => {
    if (!Array.isArray(groups)) return [];
    return groups
      .map((g) => Number(typeof g === "object" ? (g?.id ?? g?.value) : g))
      .filter((id) => Number.isFinite(id));
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) {
      toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" });
      return;
    }
    if (Name.trim().length < 3) {
      toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" });
      return;
    }
    const activityCodes = Object.values(activitiesByType)
      .flat()
      .map((option) => option.value)
      .filter((value) => value != null && String(value).trim() !== "");
    const ValuesActivityMedical = toIdArray(medicalActivityGroups);

    // Either an Activity Code OR a Medical Group satisfies the requirement.
    if (activityCodes.length === 0 && ValuesActivityMedical.length === 0) {
      toast({
        title: "Error",
        description: "Select at least one Activity Code or Medical Group.",
        variant: "destructive",
      });
      return;
    }

    try {
      const updatedItem = {
        id,
        name: Name.trim(),
        description: Description,
        activity_code: activityCodes,
        medical_groups: ValuesActivityMedical,
        is_active: isActive,
      };
      const response = await ActivityMedicalAxiosInstance.put(`updateActivityMedicalGroup`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity Medical group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─── Table Columns ────────────────────────────────────────────────────────
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      enableSorting: false,
      header: () => <div className="w-full text-center">ID</div>,
   cell: ({ row }) => (
  <div className="text-center w-full font-medium">
    {currentPage * pageSize + row.index + 1}
  </div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "activity_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity Codes {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const activityPairs = getActivitiesWithTypes(item);
        const activities = activityPairs.map(({ code }) => code);
        const medical: any[] = item.medical_groups || [];
        const type = getPrimaryActivityType(item);
        const col = getActivityTypeColor(type);
        const display = activityPairs.slice(0, 3);
        const total = activities.length + medical.length;
        return (
          <div className="w-full px-2 py-1.5">
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {display.map(({ code, type: codeType }, idx: number) => {
                const codeColor = getActivityTypeColor(codeType || type);
                return (
                  <div key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 6, background: codeColor.bg, border: `1px solid ${codeColor.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: codeColor.text }}>{code}</span>
                    {(codeType || type) && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: codeColor.text, opacity: 0.7, textTransform: "uppercase" }}>{codeType || type}</span>
                    )}
                  </div>
                );
              })}
              {medical.slice(0, Math.max(0, 3 - display.length)).map((g: any, idx: number) => (
                <span key={`medical-${idx}`} style={{ display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: 6, fontSize: 11, ...linkedGroupChipStyle(g, "#faf5ff", "#7e22ce", "#e9d5ff") }}>{linkedGroupText(g)}</span>
              ))}
              {total > 3 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewCodes([
                      ...activityPairs.map(({ code, type: codeType }) => `[${codeType || type}] ${code}`),
                      ...medical.map((g: any) => `[Medical] ${linkedGroupText(g)}`),
                    ]);
                    setIsViewDialogOpen(true);
                  }}
                  style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, cursor: "pointer" }}>
                  +{total - 3} more →
                </button>
              )}
            </div>
            <div style={{ textAlign: "center", fontSize: 10, color: "#94a3b8" }}>
              {total} total {medical.length > 0 && `· ${medical.length} medical`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Description {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <div className="w-full text-center">{row.getValue("description")}</div>,
    },
    {
      accessorKey: "is_active",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Is Active {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActive(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center">View</Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button onClick={() => handleOpenDialog(item)} className="text-blue-600 hover:text-blue-800" title="View Details">
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="activity-medical-group">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-stethoscope"></i>
          </span>
          Activity Medical Group
        </h5>
      </div>

      <DataTable2
        qaIdPrefix="activity-medical-group-data-table"
        columns={columns}
        data={items.result}
        searchColumns={[]}
        addButtonLabel="Add New Group"
        showAddButton={false}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onAdd={handleAddClick}
        onEdit={handleEditClick}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onEditClick={handleEditClick}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "10px 160px 10px 16px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: 12, border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12,
            }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              {/* Add New Group */}
              <button type="button" onClick={handleAddClick}
                style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add New Group
              </button>

              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

              {/* Search Name */}
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Group Name, Activity Code, or Medical Group."
              />

              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

              {/* Status Filter */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500,
                        borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease",
                        background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent",
                        color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b",
                        boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                      }}>
                      {option.value === "true" && filterStatus === "true" && (
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />
                      )}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* ─── View Details Dialog ─── */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent
          className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-stethoscope" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedItem.name}
                      </h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>
                      Group ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
                    </p>
                  </div>
                </div>
                {/* Info cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-stethoscope", label: "Activity Codes", value: `${getActivityCodes(selectedItem).length} codes`, accent: "#60a5fa" },
                    { icon: "fa-layer-group",  label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-tag",          label: "Type",           value: getPrimaryActivityType(selectedItem) || "N/A",                      accent: "#34d399" },
                  ].map((card, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {selectedItem.description && (
                  <>
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                          <i className="fa-solid fa-align-left" style={{ fontSize: 12, color: "#16a34a" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Description</span>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <p style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{selectedItem.description}</p>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                  </>
                )}

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-stethoscope" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Codes</span>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{getActivityCodes(selectedItem).length} items</span>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                      {getActivityCodes(selectedItem).length > 0
                        ? getActivitiesWithTypes(selectedItem).map(({ code, type }: any, i: number) => (
                            <span
                              key={i}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "5px 10px",
                                borderRadius: 8,
                                fontSize: 11,
                                fontWeight: 500,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                border: "0.5px solid #bfdbfe",
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>{code}</span>
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: 9999,
                                  background: "#dbeafe",
                                  color: "#1e40af",
                                  fontSize: 10,
                                  fontWeight: 700,
                                }}
                              >
                                {type || getPrimaryActivityType(selectedItem) || "N/A"}
                              </span>
                            </span>
                          ))
                        : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No activity codes</span>}
                    </div>
                  </div>
                </div>

                {(selectedItem.medical_groups || []).length > 0 && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                          <i className="fa-solid fa-layer-group" style={{ fontSize: 12, color: "#a855f7" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Medical Activity Groups</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                        {(selectedItem.medical_groups || []).map((g: any, i: number) => (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...linkedGroupChipStyle(g, "#faf5ff", "#7e22ce", "#e9d5ff") }}>{linkedGroupText(g)}</span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Audit Trail */}
                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}
                        </span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as Array<{ user_id?: number; date?: string; action?: string; id?: number; at?: string }>)
        .slice()
        .reverse()
        .slice(0, showAllUpdates ? undefined : 4)
        .map((entry, i) => {
          // Current backend response uses { user_id, date, action? }.
          // Keep { id, at } as a fallback for older records.
          const updatedUserId = entry.user_id ?? entry.id;
          const updatedDate = entry.date ?? entry.at;

          return (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {String(resolveUserName(updatedUserId) ?? "?")[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                  {resolveUserName(updatedUserId)}
                </span>
                {entry.action && (
                  <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#eef2ff", color: "#4338ca", fontWeight: 600, border: "1px solid #c7d2fe", textTransform: "capitalize" }}>
                    {entry.action}
                  </span>
                )}
                {i === 0 && (
                  <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                    Latest
                  </span>
                )}
              </div>
              <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                {updatedDate
                  ? formatDisplayDateTime(updatedDate)
                  : "—"}
              </span>
            </div>
          );
        })}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
          
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Add / Edit Dialog ─── */}
   {/* ─── Add / Edit Dialog ─── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activity Medical Group" : "Edit Activity Medical Group"}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
        <SectionCard num={1} title="Basic Information" subtitle="Name and description">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name <span className="text-orange-500 ml-0.5">*</span></Label>
                  <Input id="name" value={Name} onChange={(e) => setName(e.target.value)} placeholder="Enter group name" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input id="description" value={Description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter description" className="mt-1" />
                </div>
              </div>
            </SectionCard>

            <SectionCard num={2} title="Activity codes" subtitle="Choose Activity Codes or a Medical Group below (at least one is required)">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="Type">Type:<span className="text-xs text-gray-400 ml-1"></span></Label>
                  <div className="mt-1">
                    <ActivitySelector onSelectActivity={handleActivityTypeChange} selectedActivityType={ActivityType} />
                  </div>
                </div>
                {ActivityType ? (
                  <ListofActivityCode
                    onChange={handleActivitiesChange}
                    type={ActivityType}
                    defaultValue={activitiesByType[ActivityType] || []}
                    clear={clearListOfActivities}
                    isValid={activityTypeisValid}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">Select a type above to choose activity codes.</p>
                )}
              </div>
            </SectionCard>

       

            <SectionCard num={3} title="Medical groups" subtitle="Select a Medical Group or Activity Codes above">
              <ListOfActivityMedical onChange={handleMedicalActivityGroupsChange} defaultValue={medicalActivityGroups} clear={clearactivitymedicalGroups} />
            </SectionCard>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setIsDialogOpen(false); clear(); }}>Cancel</Button>
            <Button onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── View Codes Dialog ─── */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>All Activity Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-64 overflow-auto my-2">
            {viewCodes.map((code, idx) => {
              const ismedical = code.startsWith("[Medical]");
              return (
                <div key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 9999, margin: "2px", background: ismedical ? "#faf5ff" : "#eff6ff", color: ismedical ? "#7e22ce" : "#1d4ed8", border: `1px solid ${ismedical ? "#e9d5ff" : "#bfdbfe"}`, fontSize: 12, fontWeight: 500 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.7 }}>
                    {ismedical ? "Medical" : (code.match(/\[(.+?)\]/)?.[1] ?? "Code").charAt(0).toUpperCase() + (code.match(/\[(.+?)\]/)?.[1] ?? "Code").slice(1).toLowerCase()}
                  </span>
                  {code.replace(/\[.+?\] /, "")}
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex justify-between">
            <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete / Status Dialog ─── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceError ? "Record Is In Use" : "Permanent Delete"}
                  </h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  {forceError && (
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                      Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className={`fa-solid ${forceError ? "fa-bolt" : "fa-trash"}`} style={{ marginRight: 8 }}></i>
                      {forceError ? "Force Delete Anyway" : "Delete Permanently"}
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete */}
              {forceAction === "softDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button onClick={() => { if (forceId) forceSoftDelete(forceId); }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Force Delete
                    </button>
                  </div>
                </>
              )}

              {/* Status */}
              {forceAction === "status" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    ⚠️ Warning
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-triangle-exclamation" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6, fontWeight: 600 }}>
                        ⚠️ “Warning: This group is currently used in predefined rule relations. Deactivating it will disable all dependent relations.”
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>deactivate</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        const item = items.result.find(i => i.id === forceId);
                        if (item) toggleActive(item, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>Deactivate Anyway
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityMedicalGroup;
